import React, { useState, useEffect } from 'react';
import { doc, updateDoc, collection } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../config/firebase';
import { formatDate } from '../utils/labelHelpers';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../contexts/ConfirmContext';
import '../styles/TranscriptManager.css';

const TranscriptManager = ({ candidateId, candidate }) => {
  const toast = useToast();
  const confirm = useConfirm();
  const [transcripts, setTranscripts] = useState([]);
  const [showAddTranscript, setShowAddTranscript] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newTranscript, setNewTranscript] = useState({
    institution: '',
    degree: '',
    graduationDate: '',
    gpa: '',
    notes: '',
    file: null
  });

  useEffect(() => {
    if (candidate?.transcripts) {
      setTranscripts(candidate.transcripts);
    }
  }, [candidate]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file type
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
      if (!allowedTypes.includes(file.type)) {
        toast.warning('Please upload a PDF or image file (JPG, PNG)');
        return;
      }

      // Check file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        toast.warning('File size must be less than 5MB');
        return;
      }
      
      setNewTranscript({ ...newTranscript, file });
    }
  };

  const handleAddTranscript = async () => {
    if (!newTranscript.institution || !newTranscript.degree) {
      toast.warning('Please fill in Institution and Degree.');
      return;
    }

    setUploading(true);
    try {
      let fileUrl = null;
      let fileName = null;

      // Upload file if provided
      if (newTranscript.file) {
        const fileRef = ref(storage, `transcripts/${candidateId}/${Date.now()}_${newTranscript.file.name}`);
        const snapshot = await uploadBytes(fileRef, newTranscript.file);
        fileUrl = await getDownloadURL(snapshot.ref);
        fileName = newTranscript.file.name;
      }

      const transcriptData = {
        // Use Firestore's push-ID generator: unique, time-ordered, no write required
        id: doc(collection(db, 'transcripts')).id,
        institution: newTranscript.institution,
        degree: newTranscript.degree,
        graduationDate: newTranscript.graduationDate,
        gpa: newTranscript.gpa,
        notes: newTranscript.notes,
        fileUrl,
        fileName,
        uploadedAt: new Date()
      };

      const updatedTranscripts = [...transcripts, transcriptData];

      await updateDoc(doc(db, 'candidates', candidateId), {
        transcripts: updatedTranscripts,
        updatedAt: new Date()
      });

      setTranscripts(updatedTranscripts);
      setNewTranscript({
        institution: '',
        degree: '',
        graduationDate: '',
        gpa: '',
        notes: '',
        file: null
      });
      setShowAddTranscript(false);
      toast.success('Transcript added.');
    } catch (error) {
      console.error('Error adding transcript:', error);
      toast.error('Error adding transcript. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleUpdateTranscript = async (transcriptId, updatedData) => {
    try {
      const updatedTranscripts = transcripts.map(transcript =>
        transcript.id === transcriptId ? { ...transcript, ...updatedData } : transcript
      );

      await updateDoc(doc(db, 'candidates', candidateId), {
        transcripts: updatedTranscripts,
        updatedAt: new Date()
      });

      setTranscripts(updatedTranscripts);
      toast.success('Transcript updated.');
    } catch (error) {
      console.error('Error updating transcript:', error);
      toast.error('Error updating transcript. Please try again.');
    }
  };

  const handleDeleteTranscript = async (transcriptId) => {
    const confirmed = await confirm({
      title: 'Delete Transcript',
      message: 'Are you sure you want to delete this transcript?',
      confirmLabel: 'Delete',
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      const transcript = transcripts.find(t => t.id === transcriptId);

      // Delete file from storage if it exists
      if (transcript?.fileUrl) {
        try {
          const fileRef = ref(storage, transcript.fileUrl);
          await deleteObject(fileRef);
        } catch (error) {
          console.warn('Could not delete file from storage:', error);
        }
      }

      const updatedTranscripts = transcripts.filter(transcript => transcript.id !== transcriptId);

      await updateDoc(doc(db, 'candidates', candidateId), {
        transcripts: updatedTranscripts,
        updatedAt: new Date()
      });

      setTranscripts(updatedTranscripts);
      toast.success('Transcript deleted.');
    } catch (error) {
      console.error('Error deleting transcript:', error);
      toast.error('Error deleting transcript. Please try again.');
    }
  };

  return (
    <div className="transcript-manager">
      <div className="manager-header">
        <h2>Academic Transcripts</h2>
        <button 
          onClick={() => setShowAddTranscript(true)}
          className="add-transcript-btn"
        >
          Add Transcript
        </button>
      </div>

      {showAddTranscript && (
        <div className="add-transcript-modal">
          <div className="modal-content">
            <h3>Add New Transcript</h3>
            <div className="transcript-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Institution *</label>
                  <input
                    type="text"
                    value={newTranscript.institution}
                    onChange={(e) => setNewTranscript({...newTranscript, institution: e.target.value})}
                    placeholder="e.g., Duke Divinity School"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Degree *</label>
                  <input
                    type="text"
                    value={newTranscript.degree}
                    onChange={(e) => setNewTranscript({...newTranscript, degree: e.target.value})}
                    placeholder="e.g., Master of Divinity"
                    required
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Graduation Date (Optional)</label>
                  <input
                    type="date"
                    value={newTranscript.graduationDate}
                    onChange={(e) => setNewTranscript({...newTranscript, graduationDate: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>GPA</label>
                  <input
                    type="text"
                    value={newTranscript.gpa}
                    onChange={(e) => setNewTranscript({...newTranscript, gpa: e.target.value})}
                    placeholder="e.g., 3.75"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={newTranscript.notes}
                  onChange={(e) => setNewTranscript({...newTranscript, notes: e.target.value})}
                  rows="3"
                  placeholder="Additional notes about this transcript..."
                />
              </div>
              <div className="form-group">
                <label>Upload Transcript File</label>
                <input
                  type="file"
                  onChange={handleFileChange}
                  accept=".pdf,.jpg,.jpeg,.png"
                />
                <small>Accepted formats: PDF, JPG, PNG (Max 5MB)</small>
              </div>
            </div>
            <div className="modal-actions">
              <button 
                onClick={() => setShowAddTranscript(false)} 
                className="cancel-btn"
                disabled={uploading}
              >
                Cancel
              </button>
              <button 
                onClick={handleAddTranscript} 
                className="save-btn"
                disabled={uploading}
              >
                {uploading ? 'Uploading...' : 'Add Transcript'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="transcripts-list">
        {transcripts.length === 0 ? (
          <div className="no-transcripts">
            <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem', opacity: 0.4 }}>🎓</div>
            <h4 style={{ margin: '0 0 0.5rem', color: '#2c3e50' }}>No transcripts on file</h4>
            <p style={{ margin: '0 0 1.25rem', color: '#6c757d', fontSize: '0.875rem' }}>
              Record degrees, institutions, graduation dates, and upload official transcript files.
            </p>
            <button onClick={() => setShowAddTranscript(true)} className="add-transcript-btn">
              Add First Transcript
            </button>
          </div>
        ) : (
          transcripts.map(transcript => (
            <TranscriptCard
              key={transcript.id}
              transcript={transcript}
              onUpdate={(updatedData) => handleUpdateTranscript(transcript.id, updatedData)}
              onDelete={() => handleDeleteTranscript(transcript.id)}
              formatDate={formatDate}
            />
          ))
        )}
      </div>
    </div>
  );
};

const TranscriptCard = ({ transcript, onUpdate, onDelete, formatDate }) => {
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState(transcript);

  const handleSave = () => {
    onUpdate(editData);
    setEditing(false);
  };

  const handleCancel = () => {
    setEditData(transcript);
    setEditing(false);
  };

  const handleDownload = () => {
    if (transcript.fileUrl) {
      window.open(transcript.fileUrl, '_blank');
    }
  };

  return (
    <div className="transcript-card">
      <div className="transcript-header">
        <div className="transcript-title">
          {editing ? (
            <div className="edit-title">
              <input
                type="text"
                value={editData.institution}
                onChange={(e) => setEditData({...editData, institution: e.target.value})}
                className="institution-input"
                placeholder="Institution"
              />
              <input
                type="text"
                value={editData.degree}
                onChange={(e) => setEditData({...editData, degree: e.target.value})}
                className="degree-input"
                placeholder="Degree"
              />
            </div>
          ) : (
            <>
              <h4>{transcript.institution}</h4>
              <p>{transcript.degree}</p>
            </>
          )}
        </div>
        <div className="transcript-actions">
          {transcript.fileUrl && (
            <button onClick={handleDownload} className="download-btn">
              Download
            </button>
          )}
          {editing ? (
            <>
              <button onClick={handleSave} className="save-btn">Save</button>
              <button onClick={handleCancel} className="cancel-btn">Cancel</button>
            </>
          ) : (
            <>
              <button onClick={() => setEditing(true)} className="edit-btn">Edit</button>
              <button onClick={onDelete} className="delete-btn">Delete</button>
            </>
          )}
        </div>
      </div>

      <div className="transcript-details">
        <div className="transcript-info">
          <div className="info-item">
            <label>Graduation Date:</label>
            {editing ? (
              <input
                type="date"
                value={editData.graduationDate}
                onChange={(e) => setEditData({...editData, graduationDate: e.target.value})}
              />
            ) : (
              <span>{formatDate(transcript.graduationDate)}</span>
            )}
          </div>
          <div className="info-item">
            <label>GPA:</label>
            {editing ? (
              <input
                type="text"
                value={editData.gpa}
                onChange={(e) => setEditData({...editData, gpa: e.target.value})}
                placeholder="GPA"
              />
            ) : (
              <span>{transcript.gpa || 'Not specified'}</span>
            )}
          </div>
          {transcript.fileName && (
            <div className="info-item">
              <label>File:</label>
              <span className="file-name">{transcript.fileName}</span>
            </div>
          )}
          {(transcript.notes || editing) && (
            <div className="info-item full-width">
              <label>Notes:</label>
              {editing ? (
                <textarea
                  value={editData.notes}
                  onChange={(e) => setEditData({...editData, notes: e.target.value})}
                  rows="3"
                />
              ) : (
                <span>{transcript.notes || 'No notes'}</span>
              )}
            </div>
          )}
          <div className="info-item">
            <label>Uploaded:</label>
            <span>{formatDate(transcript.uploadedAt?.toDate?.() || transcript.uploadedAt)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TranscriptManager;
